import { useQuery } from "@tanstack/react-query";

import { VWO_FEATURE_SERVICING_LOGIN_V2 } from "@/constants/vwo.constants";
import { parseConfigBoolean } from "@/config/auth.config";
import { getVwoFmeClient } from "@/services/vwoFmeClient";
import {
  getOrCreateVwoFmeSessionId,
  getOrCreateVwoSessionUserId,
} from "@/utils/vwoSessionUserId";

export type ServicingLoginV2RegistrationQueryData = {
  readonly useExternalRegistrationFlow: boolean;
};

/**
 * Evaluates VWO `ServicingLoginV2` once per session (TanStack Query, infinite stale time).
 * When the flag is enabled and `enableRegistrationV3` is true, users stay on the in-app registration route.
 * When the flag is enabled and `enableRegistrationV3` is false, `useExternalRegistrationFlow` is true so
 * LoginPage can send users to `registerNowUrl` when configured. When the flag is disabled, external flow
 * is not selected from this flag (in-app registration).
 */
export function useServicingLoginV2Registration() {
  const sessionUserId = getOrCreateVwoSessionUserId();
  const sessionId = getOrCreateVwoFmeSessionId();

  return useQuery({
    queryKey: ["vwo", VWO_FEATURE_SERVICING_LOGIN_V2, sessionUserId] as const,
    queryFn: async (): Promise<ServicingLoginV2RegistrationQueryData> => {
      const client = await getVwoFmeClient();
      if (!client) {
        return { useExternalRegistrationFlow: false };
      }
      try {
        const flag = await client.getFlag(VWO_FEATURE_SERVICING_LOGIN_V2, {
          id: sessionUserId,
          sessionId,
        });
        const useLocalRegistrationV3 =
          flag.isEnabled() &&
          parseConfigBoolean(flag.getVariable<unknown>("enableRegistrationV3"));
        return {
          useExternalRegistrationFlow:
            flag.isEnabled() && !useLocalRegistrationV3,
        };
      } catch (error) {
        console.error("[VWO FME] getFlag ServicingLoginV2 failed:", error);
        return { useExternalRegistrationFlow: false };
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}
